import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { cn } from '@/lib/utils/cn'
import type { ComposeFormState } from '@/lib/venue/campaignViewModel'

function FieldError({ message }: { message: string | undefined }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-500">{message}</p>
}

const TARGET_TYPE_OPTIONS: { value: ComposeFormState['targetType']; label: string }[] = [
  { value: 'FOLLOWER', label: '전체 팔로워' },
  { value: 'RESERVATION', label: '특정 예약자' },
]

/**
 * 공지 작성 — 대상 선택(요구사항 "대상 선택(전체 팔로워 | 특정 예약자 — 예약 날짜 범위·코트 필터)").
 *
 * 🚨 코트 선택 UI는 `ClosureFormModal`의 "적용 코트"(전체 코트/코트 선택 + 칩) 패턴을 그대로 재사용한다
 * — 같은 저장소에 이미 있는 "전체/선택" 2단 선택지를 새로 발명하지 않는다.
 */
export function NoticeTargetPicker({
  form,
  errors,
  courts,
  onChangeTargetType,
  onChangeField,
  onChangeApplyScope,
  onToggleCourt,
}: {
  form: ComposeFormState
  errors: { reservationFromDate?: string; reservationToDate?: string; courts?: string }
  courts: { courtId: number; name: string }[]
  onChangeTargetType: (targetType: ComposeFormState['targetType']) => void
  onChangeField: <K extends 'reservationFromDate' | 'reservationToDate'>(key: K, value: string) => void
  onChangeApplyScope: (applyScope: ComposeFormState['applyScope']) => void
  onToggleCourt: (courtId: number) => void
}) {
  return (
    <div>
      <span className="mb-1.5 block text-sm font-medium text-ink-700">발송 대상</span>
      <div role="tablist" aria-label="발송 대상" className="flex flex-wrap gap-2">
        {TARGET_TYPE_OPTIONS.map((option) => {
          const selected = form.targetType === option.value
          return (
            <button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => {
                onChangeTargetType(option.value)
              }}
              className={cn(
                'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                selected
                  ? 'border-brand-500 bg-brand-50 text-brand-500'
                  : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
              )}
            >
              {option.label}
            </button>
          )
        })}
      </div>

      {form.targetType === 'RESERVATION' ? (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="notice-target-from">
                예약 시작일
              </label>
              <Input
                id="notice-target-from"
                type="date"
                value={form.reservationFromDate}
                aria-invalid={Boolean(errors.reservationFromDate)}
                onChange={(event) => {
                  onChangeField('reservationFromDate', event.target.value)
                }}
              />
              <FieldError message={errors.reservationFromDate} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="notice-target-to">
                예약 종료일
              </label>
              <Input
                id="notice-target-to"
                type="date"
                value={form.reservationToDate}
                aria-invalid={Boolean(errors.reservationToDate)}
                onChange={(event) => {
                  onChangeField('reservationToDate', event.target.value)
                }}
              />
              <FieldError message={errors.reservationToDate} />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="notice-target-courts">
              적용 코트
            </label>
            <Select
              id="notice-target-courts"
              value={form.applyScope}
              onChange={(event) => {
                onChangeApplyScope(event.target.value as ComposeFormState['applyScope'])
              }}
            >
              <option value="ALL_COURTS">전체 코트</option>
              <option value="SELECTED_COURTS">코트 선택</option>
            </Select>
            {form.applyScope === 'SELECTED_COURTS' ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {courts.map((court) => {
                  const selected = form.courtIds.includes(court.courtId)
                  return (
                    <button
                      key={court.courtId}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => {
                        onToggleCourt(court.courtId)
                      }}
                      className={cn(
                        'rounded-full border px-3 py-1.5 text-sm transition-colors',
                        selected
                          ? 'border-brand-500 bg-brand-50 text-brand-500'
                          : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
                      )}
                    >
                      {court.name}
                    </button>
                  )
                })}
              </div>
            ) : null}
            <FieldError message={errors.courts} />
          </div>
        </div>
      ) : null}
    </div>
  )
}
